import {
  CalendarOutlined,
  CheckCircleOutlined,
  EllipsisOutlined,
  FilterOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  StopOutlined,
} from '@ant-design/icons';
import type { MenuProps, TableProps } from 'antd';
import { Button, DatePicker, Dropdown, Input, Radio, Switch, Table } from 'antd';
import type { ColumnsType, TableRowSelection } from 'antd/es/table/interface';
import dayjs, { type Dayjs } from 'dayjs';
import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PreviewImage } from '@/components/PreviewImage/PreviewImage';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { ROUTES } from '@/constants/routes';
import { useGlobalLoader } from '@/hooks/useGlobalLoader';
import { useNotify } from '@/hooks/useNotify';
import {
  useBulkDeleteCustomersMutation,
  useBulkUpdateCustomerStatusMutation,
  useDeleteCustomerMutation,
  useGetCustomersQuery,
  useUpdateCustomerMutation,
} from '@/modules/customers/customersApi';
import type { Customer, CustomerStatusFilter } from '@/types/customer';
import { formatCustomerLocation } from '@/types/customer';
import { resolveMutationResult } from '@/utils/apiResponse';
import { confirmBulkDelete, confirmDelete } from '@/utils/confirmDialog';
import { formatDateTime } from '@/utils/date';
import { resolveCustomerImageUrl } from '@/utils/media';
import { ColumnFilterPanel } from '../categories/components/ColumnFilterPanel';
import { SortableColumnTitle } from '../categories/components/SortableColumnTitle';
import { useCustomerQueryParams } from './hooks/useCustomerQueryParams';
import styles from './Customers.module.scss';

export default function Customers() {
  const notify = useNotify();
  const navigate = useNavigate();
  const { state, apiParams, patchParams, resetAll, hasActiveFiltersOrSort } = useCustomerQueryParams();
  const { data: listResult, isLoading, isFetching } = useGetCustomersQuery(apiParams);
  useGlobalLoader(isFetching && !!listResult);

  const customers = listResult?.results ?? [];
  const activeSortBy = listResult?.sortBy ?? state.sortBy;
  const activeSortOrder = listResult?.sortOrder ?? state.sortOrder;
  const total = listResult?.total ?? 0;
  const currentPage = Number(listResult?.page ?? state.page);
  const pageSize = Number(listResult?.limit ?? state.limit);

  const [searchDraft, setSearchDraft] = useState(state.search);
  const [emailDraft, setEmailDraft] = useState(state.email);
  const [cityDraft, setCityDraft] = useState(state.city);
  const [stateDraft, setStateDraft] = useState(state.state);
  const [pincodeDraft, setPincodeDraft] = useState(state.pincode);
  const [statusDraft, setStatusDraft] = useState<CustomerStatusFilter>(state.status);
  const [joinedDraft, setJoinedDraft] = useState<[Dayjs | null, Dayjs | null] | null>(() =>
    state.joinedFrom || state.joinedTo
      ? [
          state.joinedFrom ? dayjs(state.joinedFrom) : null,
          state.joinedTo ? dayjs(state.joinedTo) : null,
        ]
      : null,
  );

  const [updateCustomer] = useUpdateCustomerMutation();
  const [deleteCustomer] = useDeleteCustomerMutation();
  const [bulkUpdateStatus, { isLoading: isBulkUpdating }] = useBulkUpdateCustomerStatusMutation();
  const [bulkDelete, { isLoading: isBulkDeleting }] = useBulkDeleteCustomersMutation();
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);

  const clearSelection = () => setSelectedRowKeys([]);

  const handleSort = useCallback(
    (field: string) => {
      const nextOrder: 'asc' | 'desc' =
        state.sortBy === field ? (state.sortOrder === 'asc' ? 'desc' : 'asc') : 'asc';
      patchParams({ sortBy: field, sortOrder: nextOrder, page: 1 });
    },
    [patchParams, state.sortBy, state.sortOrder],
  );

  const handleStatusChange = useCallback(
    async (customer: Customer, isActive: boolean) => {
      const result = await resolveMutationResult(
        updateCustomer({ id: customer.id, body: { isActive } }),
      );

      if (result.ok) {
        notify.success(result.message ?? 'Status updated.');
        return;
      }

      notify.error(result.message);
    },
    [notify, updateCustomer],
  );

  const handleDelete = useCallback(
    async (customer: Customer) => {
      const confirmed = await confirmDelete(customer.name);
      if (!confirmed) return;

      const result = await resolveMutationResult(deleteCustomer(customer.id));
      if (result.ok) {
        notify.success(result.message ?? 'Customer deleted.');
        setSelectedRowKeys((keys) => keys.filter((key) => key !== customer.id));
        return;
      }

      notify.error(result.message);
    },
    [deleteCustomer, notify],
  );

  const handleBulkStatus = async (isActive: boolean) => {
    if (selectedRowKeys.length === 0) return;

    const result = await resolveMutationResult(
      bulkUpdateStatus({ ids: selectedRowKeys, isActive }),
    );

    if (result.ok) {
      notify.success(result.message ?? 'Customers updated.');
      clearSelection();
      return;
    }

    notify.error(result.message);
  };

  const handleBulkDelete = async () => {
    if (selectedRowKeys.length === 0) return;

    const confirmed = await confirmBulkDelete(selectedRowKeys.length);
    if (!confirmed) return;

    const result = await resolveMutationResult(bulkDelete({ ids: selectedRowKeys }));

    if (result.ok) {
      notify.success(result.message ?? 'Customers deleted.');
      clearSelection();
      return;
    }

    notify.error(result.message);
  };

  const handleTableChange: TableProps<Customer>['onChange'] = (pagination) => {
    const pageChanged =
      pagination.current !== undefined && pagination.current !== state.page;
    const limitChanged =
      pagination.pageSize !== undefined && pagination.pageSize !== state.limit;

    if (!pageChanged && !limitChanged) return;

    patchParams({
      ...(pageChanged && pagination.current !== undefined ? { page: pagination.current } : {}),
      ...(limitChanged && pagination.pageSize !== undefined ? { limit: pagination.pageSize } : {}),
    });
  };

  const getRowActionItems = useCallback(
    (customer: Customer): MenuProps['items'] => [
      { key: 'view', label: 'View', onClick: () => navigate(`/customers/${customer.id}`) },
      { key: 'edit', label: 'Edit', onClick: () => navigate(`/customers/${customer.id}/edit`) },
      { type: 'divider' },
      { key: 'delete', label: 'Delete', danger: true, onClick: () => handleDelete(customer) },
    ],
    [handleDelete, navigate],
  );

  const columns: ColumnsType<Customer> = useMemo(
    () => [
      {
        title: (
          <SortableColumnTitle
            label="NAME"
            field="name"
            activeField={activeSortBy}
            sortOrder={activeSortOrder}
            onSort={handleSort}
          />
        ),
        key: 'name',
        className: styles.nameCol,
        filterIcon: (filtered) => (
          <SearchOutlined className={filtered ? styles.filterActive : styles.filterIcon} />
        ),
        filteredValue: state.search ? [state.search] : null,
        filterDropdown: ({ close }) => (
          <ColumnFilterPanel
            onApply={() => {
              patchParams({ search: searchDraft.trim() || null }, { resetPage: true });
              close();
            }}
            onReset={() => {
              setSearchDraft('');
              patchParams({ search: null }, { resetPage: true });
              close();
            }}
          >
            <Input
              allowClear
              size="small"
              prefix={<SearchOutlined />}
              placeholder="Search name or email"
              value={searchDraft}
              onChange={(event) => setSearchDraft(event.target.value)}
              onPressEnter={() => {
                patchParams({ search: searchDraft.trim() || null }, { resetPage: true });
                close();
              }}
            />
          </ColumnFilterPanel>
        ),
        filterDropdownProps: {
          onOpenChange: (open) => {
            if (open) setSearchDraft(state.search);
          },
        },
        render: (_value, record) => (
          <div className={styles.nameCell}>
            <PreviewImage
              src={resolveCustomerImageUrl(record)}
              alt={record.name}
              size={36}
              shape="circle"
              className={styles.avatar}
              fallback={record.name.charAt(0).toUpperCase()}
            />
            <Link to={`/customers/${record.id}`} className={styles.nameLink}>
              {record.name}
            </Link>
          </div>
        ),
      },
      {
        title: (
          <SortableColumnTitle
            label="EMAIL"
            field="email"
            activeField={activeSortBy}
            sortOrder={activeSortOrder}
            onSort={handleSort}
          />
        ),
        dataIndex: 'email',
        key: 'email',
        filterIcon: (filtered) => (
          <SearchOutlined className={filtered ? styles.filterActive : styles.filterIcon} />
        ),
        filteredValue: state.email ? [state.email] : null,
        filterDropdown: ({ close }) => (
          <ColumnFilterPanel
            onApply={() => {
              patchParams({ email: emailDraft.trim() || null }, { resetPage: true });
              close();
            }}
            onReset={() => {
              setEmailDraft('');
              patchParams({ email: null }, { resetPage: true });
              close();
            }}
          >
            <Input
              allowClear
              size="small"
              prefix={<SearchOutlined />}
              placeholder="Search email"
              value={emailDraft}
              onChange={(event) => setEmailDraft(event.target.value)}
              onPressEnter={() => {
                patchParams({ email: emailDraft.trim() || null }, { resetPage: true });
                close();
              }}
            />
          </ColumnFilterPanel>
        ),
        filterDropdownProps: {
          onOpenChange: (open) => {
            if (open) setEmailDraft(state.email);
          },
        },
        render: (value?: string) => <span className={styles.emailCell}>{value || '—'}</span>,
      },
      {
        title: 'LOCATION',
        key: 'location',
        filterIcon: (filtered) => (
          <SearchOutlined className={filtered ? styles.filterActive : styles.filterIcon} />
        ),
        filteredValue: state.city || state.state ? ['filtered'] : null,
        filterDropdown: ({ close }) => (
          <ColumnFilterPanel
            onApply={() => {
              patchParams(
                {
                  city: cityDraft.trim() || null,
                  state: stateDraft.trim() || null,
                },
                { resetPage: true },
              );
              close();
            }}
            onReset={() => {
              setCityDraft('');
              setStateDraft('');
              patchParams({ city: null, state: null }, { resetPage: true });
              close();
            }}
          >
            <Input
              allowClear
              size="small"
              placeholder="City"
              value={cityDraft}
              onChange={(event) => setCityDraft(event.target.value)}
              style={{ marginBottom: 8 }}
            />
            <Input
              allowClear
              size="small"
              placeholder="State"
              value={stateDraft}
              onChange={(event) => setStateDraft(event.target.value)}
            />
          </ColumnFilterPanel>
        ),
        filterDropdownProps: {
          onOpenChange: (open) => {
            if (open) {
              setCityDraft(state.city);
              setStateDraft(state.state);
            }
          },
        },
        render: (_value, record) => (
          <span className={styles.locationCell}>{formatCustomerLocation(record.address)}</span>
        ),
      },
      {
        title: 'PINCODE',
        key: 'pincode',
        filterIcon: (filtered) => (
          <SearchOutlined className={filtered ? styles.filterActive : styles.filterIcon} />
        ),
        filteredValue: state.pincode ? [state.pincode] : null,
        filterDropdown: ({ close }) => (
          <ColumnFilterPanel
            onApply={() => {
              patchParams({ pincode: pincodeDraft.trim() || null }, { resetPage: true });
              close();
            }}
            onReset={() => {
              setPincodeDraft('');
              patchParams({ pincode: null }, { resetPage: true });
              close();
            }}
          >
            <Input
              allowClear
              size="small"
              placeholder="Pincode"
              value={pincodeDraft}
              onChange={(event) => setPincodeDraft(event.target.value)}
              onPressEnter={() => {
                patchParams({ pincode: pincodeDraft.trim() || null }, { resetPage: true });
                close();
              }}
            />
          </ColumnFilterPanel>
        ),
        filterDropdownProps: {
          onOpenChange: (open) => {
            if (open) setPincodeDraft(state.pincode);
          },
        },
        render: (_value, record) => (
          <span className={styles.pincodeCell}>{record.address?.pincode || '—'}</span>
        ),
      },
      {
        title: (
          <SortableColumnTitle
            label="STATUS"
            field="isActive"
            activeField={activeSortBy}
            sortOrder={activeSortOrder}
            onSort={handleSort}
          />
        ),
        dataIndex: 'isActive',
        key: 'isActive',
        className: styles.statusCol,
        align: 'center',
        filterIcon: (filtered) => (
          <FilterOutlined className={filtered ? styles.filterActive : styles.filterIcon} />
        ),
        filteredValue: state.status !== 'all' ? [state.status] : null,
        filterDropdown: ({ close }) => (
          <ColumnFilterPanel
            onApply={() => {
              patchParams({ status: statusDraft === 'all' ? null : statusDraft }, { resetPage: true });
              close();
            }}
            onReset={() => {
              setStatusDraft('all');
              patchParams({ status: null }, { resetPage: true });
              close();
            }}
          >
            <Radio.Group
              value={statusDraft}
              onChange={(event) => setStatusDraft(event.target.value)}
              className={styles.statusRadio}
            >
              <Radio value="all">All</Radio>
              <Radio value="active">Active</Radio>
              <Radio value="inactive">Inactive</Radio>
            </Radio.Group>
          </ColumnFilterPanel>
        ),
        filterDropdownProps: {
          onOpenChange: (open) => {
            if (open) setStatusDraft(state.status);
          },
        },
        render: (isActive: boolean, record) => (
          <Switch
            checked={isActive}
            size="small"
            onChange={(checked) => handleStatusChange(record, checked)}
          />
        ),
      },
      {
        title: (
          <SortableColumnTitle
            label="JOINED"
            field="createdAt"
            activeField={activeSortBy}
            sortOrder={activeSortOrder}
            onSort={handleSort}
          />
        ),
        dataIndex: 'createdAt',
        key: 'createdAt',
        filterIcon: (filtered) => (
          <CalendarOutlined className={filtered ? styles.filterActive : styles.filterIcon} />
        ),
        filteredValue: state.joinedFrom || state.joinedTo ? ['filtered'] : null,
        filterDropdown: ({ close }) => (
          <ColumnFilterPanel
            onApply={() => {
              patchParams(
                {
                  joinedFrom: joinedDraft?.[0]?.format('YYYY-MM-DD') || null,
                  joinedTo: joinedDraft?.[1]?.format('YYYY-MM-DD') || null,
                },
                { resetPage: true },
              );
              close();
            }}
            onReset={() => {
              setJoinedDraft(null);
              patchParams({ joinedFrom: null, joinedTo: null }, { resetPage: true });
              close();
            }}
          >
            <DatePicker.RangePicker
              size="small"
              value={joinedDraft}
              onChange={(values: [Dayjs | null, Dayjs | null] | null) => setJoinedDraft(values)}
              style={{ width: '100%' }}
            />
          </ColumnFilterPanel>
        ),
        filterDropdownProps: {
          onOpenChange: (open) => {
            if (open) {
              setJoinedDraft(
                state.joinedFrom || state.joinedTo
                  ? [
                      state.joinedFrom ? dayjs(state.joinedFrom) : null,
                      state.joinedTo ? dayjs(state.joinedTo) : null,
                    ]
                  : null,
              );
            }
          },
        },
        render: (value: string) => <span className={styles.dateCell}>{formatDateTime(value)}</span>,
      },
      {
        title: 'ACTION',
        key: 'actions',
        align: 'center',
        render: (_value, record) => (
          <Dropdown menu={{ items: getRowActionItems(record) }} trigger={['click']}>
            <Button type="text" size="small" icon={<EllipsisOutlined />} className={styles.actionBtn} />
          </Dropdown>
        ),
      },
    ],
    [
      activeSortBy,
      activeSortOrder,
      cityDraft,
      emailDraft,
      getRowActionItems,
      handleSort,
      handleStatusChange,
      joinedDraft,
      patchParams,
      pincodeDraft,
      searchDraft,
      state.city,
      state.email,
      state.joinedFrom,
      state.joinedTo,
      state.pincode,
      state.search,
      state.state,
      state.status,
      stateDraft,
      statusDraft,
    ],
  );

  const rowSelection: TableRowSelection<Customer> = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys as string[]),
  };

  return (
    <div className={styles.page}>
      <PageHeader
        actions={
          <>
            {hasActiveFiltersOrSort ? (
              <Button
                type="default"
                size="small"
                className={styles.headerBtn}
                icon={<ReloadOutlined />}
                onClick={() => {
                  setSearchDraft('');
                  setEmailDraft('');
                  setCityDraft('');
                  setStateDraft('');
                  setPincodeDraft('');
                  setStatusDraft('all');
                  setJoinedDraft(null);
                  resetAll();
                }}
              >
                Reset
              </Button>
            ) : null}
            <Button
              type="primary"
              size="small"
              className={styles.addBtn}
              icon={<PlusOutlined />}
              onClick={() => navigate(ROUTES.CUSTOMER_CREATE)}
            >
              Add customer
            </Button>
          </>
        }
      />

      {selectedRowKeys.length > 0 ? (
        <div className={styles.bulkBar}>
          <span className={styles.bulkCount}>{selectedRowKeys.length} selected</span>
          <Button
            size="small"
            icon={<CheckCircleOutlined />}
            onClick={() => handleBulkStatus(true)}
            loading={isBulkUpdating}
          >
            Activate
          </Button>
          <Button
            size="small"
            icon={<StopOutlined />}
            onClick={() => handleBulkStatus(false)}
            loading={isBulkUpdating}
          >
            Deactivate
          </Button>
          <Button size="small" danger onClick={handleBulkDelete} loading={isBulkDeleting}>
            Delete
          </Button>
          <Button size="small" type="text" onClick={clearSelection}>
            Clear
          </Button>
        </div>
      ) : null}

      <div className={styles.tableCard}>
        <Table<Customer>
          className={styles.table}
          rowKey="id"
          size="small"
          scroll={{ x: 'max-content' }}
          columns={columns}
          dataSource={customers}
          loading={isLoading && !listResult}
          rowSelection={rowSelection}
          pagination={{
            current: currentPage,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50],
            showTotal: (value) => `${value} customers`,
            size: 'small',
          }}
          onChange={handleTableChange}
        />
      </div>
    </div>
  );
}
